import * as React from "react";
import { ImageIcon } from "lucide-react";
import { PostStatsRow } from "@/components/PostStatsRow";
import { Avatar, Badge } from "@/components/ui";
import { formatRelativeTime } from "@/components/reviews";
import { socialIcons, socialNetworkNames } from "@/interfaces";
import { AspectRatio } from "@/services/bedrock/dtos/DesignPackage";
import { extractInitials } from "@/utils/helpers";
import { PostAggregate } from "../performance";

const ASPECT_CLASS: Record<AspectRatio, string> = {
  square_1_1: "aspect-square",
  portrait_4_5: "aspect-[4/5]",
  portrait_9_16: "aspect-[9/16]",
};

export interface PostPerformanceCardProps {
  post: PostAggregate;
  aspectRatio: AspectRatio;
  isTop?: boolean;
  onOpen: (post: PostAggregate) => void;
}

export function PostPerformanceCard({ post, aspectRatio, isTop, onOpen }: PostPerformanceCardProps): JSX.Element {
  return (
    <button
      type="button"
      aria-label={`Open post by ${post.playerName}`}
      onClick={() => onOpen(post)}
      className="flex w-full items-start gap-3 border border-slate-200 rounded-xl p-2 bg-white hover:shadow-md cursor-pointer text-left transition-shadow"
      data-testid="post-performance-card"
    >
      <div
        className={`w-14 shrink-0 rounded-md overflow-hidden bg-slate-100 flex items-center justify-center ${ASPECT_CLASS[aspectRatio]}`}
      >
        {post.thumbnailUrl ? (
          <img src={post.thumbnailUrl} alt="" className="size-full object-cover" />
        ) : (
          <ImageIcon className="size-5 text-slate-400" aria-hidden="true" />
        )}
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-1.5">
        <div className="flex min-w-0 items-center gap-2">
          <Avatar size="sm">
            <Avatar.Fallback>{extractInitials(post.playerName)}</Avatar.Fallback>
          </Avatar>
          <span className="min-w-0 truncate text-sm font-semibold text-slate-800">{post.playerName}</span>
          <span className="flex shrink-0 items-center gap-1 text-xs text-slate-500">
            {/* The counts sum every network the post went to, so every network is shown. */}
            {post.networks.map(network => (
              <img key={network} src={socialIcons[network]} className="size-4" alt={socialNetworkNames[network]} />
            ))}
            {formatRelativeTime(post.postedAt)}
          </span>
          {isTop && (
            <Badge className="ml-auto shrink-0 rounded-full border-transparent bg-amber-100 text-amber-800 hover:bg-amber-100">
              Top
            </Badge>
          )}
        </div>
        <PostStatsRow counts={post} />
      </div>
    </button>
  );
}
